import { LAYER } from './layers';
import { CountBadge } from './CountBadge';
import { Wand2, X } from 'lucide-react';
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { Checkbox } from './inputs/Checkbox';
import { SkeletonFields } from './Skeleton';
import { Toggle } from './inputs/Toggle';
import { ModalFooterSurfaceContext } from './modalFooterSurface';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'fullscreen';

const SIZES: Record<ModalSize, string> = {
  // `sm` is reserved for pure confirmations and info popovers — anything with fields is lg+.
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-4xl',
  '3xl': 'max-w-6xl',
  fullscreen: 'max-w-none w-[calc(100vw-2rem)] h-[calc(100vh-2rem)]',
};

/**
 * Lets a host render a modal's content as an in-flow panel instead of an overlay.
 * A provider with `inline: true` suppresses the portal, backdrop, and Esc handling.
 */
export const ModalSurfaceContext = createContext<{ inline: boolean }>({ inline: false });

/**
 * The confirm sentence. Identical on every modal in the product, defined once here so it
 * cannot be reworded per surface — there were five variants before it moved.
 *
 * It asks the user to confirm the action and nothing more. A consequence worth stating
 * belongs on the form itself, where it can be read before the box is ticked.
 */
export const CONFIRM_SENTENCE = 'I confirm this action.';

/**
 * The confirm checkbox every transaction modal gates its primary button behind.
 *
 * It lives on `<Modal>` rather than in each modal's body so it cannot drift: one position
 * (pinned above the footer, outside the scrolling body, so a long form never scrolls it
 * away from the button it gates), one wording, one set of styles. Hand-rolling this markup
 * in a feature component is drift — there were five copies before it moved here.
 *
 * On a stepped modal the caller passes it on the last step only.
 */
export interface ModalConfirm {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Explicit so existing ids stay stable; defaults to `${modal id}-confirm`. */
  id?: string;
}

/**
 * The second sentence, under the confirm tick. Fixed like `CONFIRM_SENTENCE` and for the same
 * reason — one wording, defined once, with no override at the call site.
 */
export const ADD_ANOTHER_SENTENCE = 'I wish to add more.';

/**
 * "Keep this form open after it saves, so the next record can be entered."
 *
 * **CREATE ONLY, and that is the CALLER's job** — the same division `autogenerate` and the
 * last-step `confirm` already use, because the shell cannot know whether it is over a new
 * record or an existing one. On an edit it would mean "save this record and then edit it
 * again", which is not a thing anybody asks for.
 *
 * **It is UNTICKED by default and STAYS TICKED across the loop.** Those are two different
 * questions and the answers differ: the reader who has not said otherwise wants one record
 * (so, unticked), but the reader who ticked it is adding a run of them and must not re-tick
 * before every single save. The CONFIRM tick is the opposite — it resets every time, because
 * it is a statement about one write. Only closing the modal clears this one.
 */
export interface ModalAddAnother {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Explicit so existing ids stay stable; defaults to `${modal id}-add-another`. */
  id?: string;
}

/**
 * Fills a create form with synthetic values so the primary action enables on the first try.
 *
 * **Create only, never edit.** On an edit form it would overwrite the record's real values
 * with test data, which is the one thing a form over existing data must not do — so a modal
 * that serves both passes this only when it is creating.
 */
export interface ModalAutogenerate {
  /**
   * Fills every REQUIRED field. Optional fields are the caller's choice, and anything the
   * caller cannot write imperatively — an uncontrolled rich-text document, a file the reader
   * has to choose — is out of reach and must not be faked around.
   */
  onGenerate: () => void;
  disabled?: boolean;
  /** Explicit so existing ids stay stable; defaults to `${modal id}-autogenerate`. */
  id?: string;
}

interface ModalProps {
  open: boolean;
  /** Title only — modals never carry a subtitle. */
  title: string;
  /**
   * A COUNT beside the title, as the shared `<CountBadge>` — how many rows the form holds
   * (product call, 12 Oct 2026; the questionnaire form's question count). Absent draws nothing;
   * `0` draws a badge. It is in the heading, so the accessible name reads "New questionnaire 4".
   */
  titleCount?: number;
  onClose: () => void;
  closeOnBackdrop?: boolean;
  size?: ModalSize;
  /** Renders only when set. */
  footer?: ReactNode;
  /**
   * Required on every modal that writes. Resetting it on open is the caller's job — a
   * previous attempt must never leave the box ticked.
   */
  confirm?: ModalConfirm;
  /** CREATE ONLY, and the caller decides that — see `ModalAddAnother`. */
  addAnother?: ModalAddAnother;
  /**
   * Controls for the footer's LEFT, FIRST in the leading group — before Add more and Autogenerate, in
   * the same `mr-auto` wrapper (a second would split the row). For moving between records, not for a
   * form's own actions: the deliberation profile's previous/next applicant (product call, 20 Oct 2026).
   */
  footerLeading?: ReactNode;
  /**
   * Renders the standard Autogenerate button in the footer's leading slot.
   *
   * A prop rather than markup, for the reason the confirm tick is one: the button had a
   * fixed variant, icon, label, and `mr-auto` placement that every create modal was
   * expected to reproduce by hand, and the second hand-rolled copy is where a product-wide
   * affordance starts drifting into per-screen ones. The shell owns the appearance; the
   * caller owns only what "generate" means for its own fields.
   */
  autogenerate?: ModalAutogenerate;
  /**
   * False turns the body into a bounded flex column so exactly one inner region owns the
   * scroll — used when the body embeds its own scrolling table.
   */
  bodyScroll?: boolean;
  /**
   * The form is still FETCHING what its controls will show — a catalog behind a picker, the
   * record behind an edit, a seeded default the server decides.
   *
   * While it is true the body renders `<SkeletonFields>` INSTEAD of `children`. Rendering the
   * real controls disabled was the old shape and it reads as a form that is simply broken:
   * empty boxes the reader may click and not type in, with nothing saying why. A skeleton says
   * "not yet" in the one vocabulary the rest of the product already uses for it.
   *
   * **It is the SHELL's job, not the call site's**, for `ModalFooterSurfaceContext`'s reason —
   * a treatment every modal must share is one no modal should have to remember. The call site
   * owns only the boolean, and it is the same boolean it already computes to disable the
   * primary action.
   *
   * **A modal that fetches NOTHING never passes it.** Most creates are a blank form over a
   * catalog that is already in hand; a skeleton there is a flash of grey for no reason.
   */
  loading?: boolean;
  /**
   * How many placeholder fields to draw, and in how many columns — the shape of the form that
   * is coming, so nothing moves when it lands. Defaults to four in the `sm:grid-cols-2` grid.
   */
  loadingFields?: { rows?: number; columns?: 1 | 2 };
  /** Derives `${id}-title` for `aria-labelledby`. */
  id?: string;
  children: ReactNode;
}

/**
 * The canonical modal shell — reference §14.
 *
 * Portals to `document.body` so `position: fixed` escapes transformed or `overflow`
 * ancestors. On open it locks body scroll, installs Esc-to-close, and moves focus to the
 * first focusable element (skipping `[data-modal-close]` and `[data-modal-skip-focus]`, so
 * focus lands on the first field rather than the X or a help icon).
 *
 * Initial focus only — deliberately no focus-trap loop, per the reference. A trap fights
 * screen-reader virtual cursors and browser UI; the overlay plus Esc is the documented
 * behaviour and stays consistent across every dialog.
 *
 * **The tab run is the fields, the confirm tick, then the primary action**, in DOM order,
 * which is reading order. The X and the footer's non-action buttons carry `tabIndex={-1}`;
 * so does the label help icon. See "Tab runs through the fields to the primary action" in
 * CLAUDE.md for what stays in and why.
 */
export function Modal({
  open,
  title,
  titleCount,
  onClose,
  closeOnBackdrop = true,
  size = 'md',
  footer,
  autogenerate,
  confirm,
  addAnother,
  footerLeading,
  bodyScroll = true,
  loading = false,
  loadingFields,
  id,
  children,
}: ModalProps) {
  const { inline } = useContext(ModalSurfaceContext);
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open || inline) {
      return;
    }

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;

    if (panel) {
      /*
        Initial focus lands on the first thing the user came to fill in.

        `[data-modal-close]` keeps it off the X. `[data-modal-skip-focus]` keeps it off
        disclosure controls that sit before the field they belong to — a `<HelpTip>` lives in
        the label row, so without this the first field's help icon wins, and a tooltip that
        opens on focus appears unprompted the moment the modal does.
      */
      const skip = ':not([data-modal-close]):not([data-modal-skip-focus])';

      const focusable = panel.querySelector<HTMLElement>(
        [
          `a[href]${skip}`,
          `button:not([disabled])${skip}`,
          `input:not([disabled])${skip}`,
          `select:not([disabled])${skip}`,
          `textarea:not([disabled])${skip}`,
          `[tabindex]:not([tabindex="-1"])${skip}`,
        ].join(', '),
      );

      (focusable ?? panel).focus();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus();
    };
  }, [open, inline, onClose]);

  if (!open) {
    return null;
  }

  const titleId = `${id ?? 'modal'}-title`;

  const panel = (
    <div
      ref={panelRef}
      id={id}
      role="dialog"
      aria-modal={inline ? undefined : true}
      aria-labelledby={titleId}
      tabIndex={-1}
      className={[
        'relative flex w-full flex-col rounded-2xl bg-white focus:outline-none',
        inline ? 'border border-slate-200' : 'shadow-overlay',
        SIZES[size],
        size === 'fullscreen' ? '' : 'max-h-[70vh]',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-6 py-3">
        <h2 id={titleId} className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-800">
          {title}
          <CountBadge count={titleCount} />
        </h2>
        {/*
          Out of the tab run: it sits before every field in the DOM, so it took the first Tab
          on every modal in the product — and Esc already closes, so the function is not lost.
          Still focusable programmatically and still announced, hence `tabIndex={-1}` rather
          than removing the button.
        */}
        <button
          type="button"
          data-modal-close
          tabIndex={-1}
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>

      <div
        aria-busy={loading || undefined}
        className={[
          'min-h-0 flex-1 px-6 py-5',
          bodyScroll ? 'modern-scrollbar overflow-y-auto' : 'flex flex-col overflow-hidden',
        ].join(' ')}
      >
        {/*
          The skeleton REPLACES the body rather than covering it. The form state lives in the
          caller, never in these children, so unmounting them loses nothing — and leaving the
          real controls mounted underneath would put focusable, empty fields in the tab run
          behind a placeholder, which is the defect this is here to remove.
        */}
        {loading ? (
          <SkeletonFields rows={loadingFields?.rows} columns={loadingFields?.columns} />
        ) : (
          children
        )}
      </div>

      {confirm ? <ConfirmStrip confirm={confirm} modalId={id} /> : null}

      {/*
        Buttons are RIGHT-aligned, always. The primary action sits at the end of the reading
        line and on the same edge in every modal in the product, so it is found by position
        rather than by reading the row. `justify-end` lives here rather than in each footer,
        which is what makes that true of modals nobody has revisited.

        A footer that genuinely needs something on the left — a destructive action set apart
        from Save, a secondary link — puts it in a `<div className="mr-auto">`, and the rest
        still lands on the right.
      */}
      {footer || footerLeading || autogenerate || addAnother ? (
        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 px-6 py-3">
          {/*
            The footer publishes itself so `<Button>` can keep the tab run to the fields, the
            confirm tick and the primary action — see `modalFooterSurface.ts`. A context
            rather than a prop on every Cancel, for the reason the confirm sentence lives
            here: a rule spelled out at nineteen call sites is a rule that drifts at one.
          */}
          <ModalFooterSurfaceContext.Provider value={true}>
          {/*
            Leading slot: Autogenerate is not one of the form's own actions and must not sit
            in the run of buttons the reader chooses between. Wrapped in a `mr-auto` div
            rather than carrying the margin itself, per "Modal footers are right-aligned" —
            auto margins resolve before `justify-content`, and the wrapper is the form that
            keeps the rest of the row on the right.
          */}
          {/*
            ADD MORE SITS IN THE LEADING SLOT, BEFORE AUTOGENERATE (product call, 16 Oct 2026,
            moving it out of the confirm strip where it first shipped).

            One `mr-auto` wrapper holds BOTH, because two of them would resolve independently
            and push the second into the middle of the row — auto margins are distributed
            before `justify-content` decides anything, which is the same trap the footer's own
            comment names.

            **It is a `<Toggle>`, not a `<Button>`, and that is load-bearing here**: inside
            this footer `<Button>` reads the surface context and drops itself to
            `tabIndex={-1}`, which is right for Cancel and Autogenerate and wrong for a control
            the reader operates. The switch keeps its place in the tab run.
          */}
          {footerLeading || autogenerate || addAnother ? (
            <div className="mr-auto flex items-center gap-3">
              {footerLeading}
              {addAnother ? (
                <Toggle
                  id={addAnother.id ?? `${id ?? 'modal'}-add-another`}
                  checked={addAnother.checked}
                  onChange={addAnother.onChange}
                  disabled={addAnother.disabled}
                  label={ADD_ANOTHER_SENTENCE}
                />
              ) : null}
              {autogenerate ? (
                <Button
                  id={autogenerate.id ?? `${id ?? 'modal'}-autogenerate`}
                  variant="tertiary"
                  leading={<Wand2 className="h-4 w-4" />}
                  onClick={autogenerate.onGenerate}
                  disabled={autogenerate.disabled}
                >
                  Autogenerate
                </Button>
              ) : null}
            </div>
          ) : null}

          {footer}
          </ModalFooterSurfaceContext.Provider>
        </footer>
      ) : null}
    </div>
  );

  if (inline) {
    return panel;
  }

  return createPortal(
    <div className={`fixed inset-0 ${LAYER.MODAL} flex items-center justify-center p-4`}>
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      {panel}
    </div>,
    document.body,
  );
}

/**
 * Pinned between the body and the footer: the box stays beside the button it gates even
 * when the form above it scrolls.
 */
function ConfirmStrip({ confirm, modalId }: { confirm: ModalConfirm; modalId?: string }) {
  const confirmId = confirm.id ?? `${modalId ?? 'modal'}-confirm`;

  return (
    <div className="shrink-0 border-t border-slate-200 px-6 py-3">
      <label
        htmlFor={confirmId}
        className="flex cursor-pointer items-start gap-2 text-sm text-slate-600"
      >
        <Checkbox
          id={confirmId}
          name={confirmId}
          checked={confirm.checked}
          onChange={(e) => confirm.onChange(e.target.checked)}
          disabled={confirm.disabled}
        />
        <span>{CONFIRM_SENTENCE}</span>
      </label>
    </div>
  );
}
